import AddIcon from '@mui/icons-material/Add';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import CircleIcon from '@mui/icons-material/Circle';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    InputBase,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    TablePagination,
    Theme,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useCognitoEmailLookup from '../../hooks/useCognitoEmailLookup';
import useCognitoGlobalSignOut from '../../hooks/useCognitoGlobalSignOut';
import useHostessStatus from '../../hooks/useHostessStatus';
import useSubscribersAdmin from '../../hooks/useSubscribersAdmin';
import { SettingsContext } from '../../store/SettingsContext';
import { MuiColorPalletName } from '../../utils/common';
import CognitoEmailLookupDialog from './CognitoEmailLookupDialog';

const SquareChip = (props: { color: string; value: string | React.ReactNode; variant: 'filled' | 'outlined' }) => {
    const theme: Theme = useTheme();

    return (
        <div
            style={{
                backgroundColor: props.variant === 'filled' ? props.color : 'transparent',
                color: props.variant === 'filled' ? theme.palette.getContrastText(props.color) : props.color,
                border: props.variant === 'outlined' ? `1px solid ${props.color}` : 'none',
                padding: '2px 5px',
                borderRadius: theme.shape.borderRadius,
                fontSize: '10px',
                textAlign: 'center',
            }}
        >
            {props.value}
        </div>
    );
};

const UsernameCell = (props: { subscriber: any }) => {
    const { subscriber } = props;
    const theme: Theme = useTheme();
    const [showCopy, setShowCopy] = useState<boolean>(false);

    return (
        <div
            onMouseOver={() => setShowCopy(true)}
            onMouseOut={() => setShowCopy(false)}
            style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%',
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" justifyContent={'space-between'}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <CircleIcon color={subscriber.isLoggedIn ? 'success' : 'error'} sx={{ fontSize: 8 }} />
                    <div onClick={() => navigator.clipboard.writeText(subscriber.username)} style={{ cursor: 'pointer' }}>
                        {subscriber.username}
                    </div>
                    <ContentCopyIcon sx={{ fontSize: 12, visibility: showCopy ? 'visible' : 'hidden' }} />
                </Stack>
                <SquareChip
                    variant="outlined"
                    color={
                        typeof subscriber.ofSubscriptionPrice === 'number'
                            ? subscriber.ofSubscriptionPrice
                                ? theme.palette.primary.main
                                : theme.palette.secondary.main
                            : theme.palette.grey[300]
                    }
                    value={typeof subscriber.ofSubscriptionPrice === 'number' ? (subscriber.ofSubscriptionPrice ? 'P' : 'F') : '?'}
                />
            </Stack>
        </div>
    );
};

const timestampToTimeLapsedString = (timestamp: number) => {
    const seconds = moment().diff(moment(timestamp), 'seconds');

    if (seconds < 60) {
        return `${seconds}s`;
    }

    if (seconds < 3600) {
        return `${Math.floor(seconds / 60)}m`;
    }

    if (seconds < 86400) {
        return `${Math.floor(seconds / 3600)}h`;
    }

    return `${Math.floor(seconds / 86400)}d`;
};

const isHostessRequired = (subscriber: any): boolean => {
    if (!subscriber.isLoggedIn) {
        return false;
    }

    if (
        subscriber.sextforce &&
        subscriber.sextforce.active &&
        moment(subscriber.sextforce.paidDate).isAfter(moment().subtract(1, 'month').subtract(1, 'day'))
    ) {
        return true;
    }

    if (
        subscriber.housekeeping &&
        subscriber.housekeeping.active &&
        moment(subscriber.housekeeping.paidDate).isAfter(moment().subtract(1, 'month').subtract(1, 'day'))
    ) {
        return true;
    }

    if (
        subscriber.inCommonFans &&
        subscriber.inCommonFans.active &&
        moment(subscriber.inCommonFans.paidDate).isAfter(moment().subtract(1, 'month').subtract(1, 'day'))
    ) {
        return true;
    }

    if (
        subscriber.messageUsers &&
        subscriber.messageUsers.autoMessageNewSubscriber &&
        subscriber.messageUsers.autoMessageNewSubscriber.active &&
        subscriber.messageUsers.autoMessageNewSubscriber.message !== ''
    ) {
        return true;
    }

    if (
        subscriber.messageUsers &&
        subscriber.messageUsers.autoMessageReturningSubscriber &&
        subscriber.messageUsers.autoMessageReturningSubscriber.active &&
        subscriber.messageUsers.autoMessageReturningSubscriber.message !== ''
    ) {
        return true;
    }

    if (
        subscriber.messageUsers &&
        subscriber.messageUsers.autoMessageNewTrialSubscriber &&
        subscriber.messageUsers.autoMessageNewTrialSubscriber.active &&
        subscriber.messageUsers.autoMessageNewTrialSubscriber.message !== ''
    ) {
        return true;
    }

    return false;
};

const HostessTimestamp = (props: { subscriber: any }) => {
    const { subscriber } = props;
    const hostessKeys: { connected: string; lastMessageTimestamp: string } | null = subscriber._hostess || null;
    const { setSubscriberHostessStatus, setSubscriberHostessStatusLoading } = useHostessStatus();

    const [color, setColor] = useState<'inherit' | 'error' | 'success' | 'primary' | 'secondary' | 'info' | 'warning'>('success');
    const [required, setRequired] = useState<'text' | 'outlined' | 'contained'>('outlined');

    useEffect(() => {
        let c: 'inherit' | 'error' | 'success' | 'primary' | 'secondary' | 'info' | 'warning' = 'success';
        let r: 'text' | 'outlined' | 'contained' = 'outlined';

        if (!hostessKeys || !hostessKeys.connected || hostessKeys.connected === '0') {
            c = 'error';
        } else if (
            !hostessKeys.lastMessageTimestamp ||
            moment(parseInt(hostessKeys.lastMessageTimestamp)).isBefore(moment().subtract(5, 'minute'))
        ) {
            c = 'warning';
        }

        if (isHostessRequired(subscriber)) {
            r = 'contained';
        }

        if (r === 'outlined' && c === 'error') {
            c = 'inherit';
        }

        setColor(c);
        setRequired(r);
    }, [hostessKeys, subscriber]);

    const handleHostessStatus = async (action: 'add' | 'remove') => {
        const doChange = async () => {
            setSubscriberHostessStatus(subscriber._id, action)
                .then(result => {
                    if (result.res && result.res === 'success') {
                        setColor(action === 'add' ? 'success' : 'error');
                    }
                })
                .catch(() => {});
        };

        doChange();
    };

    return (
        <div style={{ textAlign: 'center' }}>
            {hostessKeys && hostessKeys.connected && hostessKeys.connected === '1' ? (
                <LoadingButton
                    variant="contained"
                    size="small"
                    color={color}
                    loading={setSubscriberHostessStatusLoading}
                    onClick={() => {
                        handleHostessStatus('remove');
                    }}
                >
                    <span>{timestampToTimeLapsedString(parseInt(hostessKeys.lastMessageTimestamp))}</span>
                </LoadingButton>
            ) : (
                <LoadingButton
                    variant={required}
                    size="small"
                    color={color}
                    loading={setSubscriberHostessStatusLoading}
                    onClick={() => {
                        handleHostessStatus('add');
                    }}
                >
                    <span>Off</span>
                </LoadingButton>
            )}
        </div>
    );
};

const serviceStatus = (subscriber: any, serviceDataKey: string, alwaysActive: boolean): { color: MuiColorPalletName; label: string } => {
    const data = subscriber?.[serviceDataKey];

    if (!data) {
        return { color: 'disabled', label: 'Not Setup' };
    }

    // alwaysActive services (Follow-Back, Messaging) don't require payment; older records may lack
    // the active flag entirely, so only an explicit false counts as inactive
    const active = alwaysActive ? data.active !== false : !!data.active;

    if (!active) {
        return { color: 'disabled', label: 'Not Active' };
    }

    if (alwaysActive) {
        return { color: 'success', label: 'Active' };
    }

    // A payment covers one month; the service stays paid up until the end of that day in GMT
    // (see isSextforceActive() in utils/common for the reference calculation)
    const paid = data.paidDate && moment.utc(data.paidDate).add(1, 'month').endOf('day').isSameOrAfter(moment.utc());

    return paid ? { color: 'success', label: 'Active' } : { color: 'warning', label: 'Payment Due' };
};

const ServicesCell = (props: { subscriber: any }) => {
    const { subscriber } = props;
    const settingsContext = useContext(SettingsContext);

    return (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
            {Object.keys(settingsContext.services)
                .filter(serviceKey => settingsContext.services[serviceKey].visible)
                .map(serviceKey => {
                    const service = settingsContext.services[serviceKey];
                    const { color, label } = serviceStatus(subscriber, service.serviceNameKey ?? serviceKey, service.alwaysActive);

                    return (
                        <Tooltip key={serviceKey} title={`${service.name}: ${label}`}>
                            <span style={{ display: 'flex' }}>{service.icon(color, 'small')}</span>
                        </Tooltip>
                    );
                })}
        </Stack>
    );
};

const ActionsCell = (props: {
    subscriber: any;
    onLookupCognitoEmail: (subscriber: any) => void;
    onCognitoGlobalSignOut: (subscriber: any) => void;
}) => {
    const { subscriber, onLookupCognitoEmail, onCognitoGlobalSignOut } = props;
    const hasCognito = !!subscriber.cognito?.username;
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    return (
        <>
            <IconButton size="small" component={Link} to={`/subscribers/${subscriber._id}`}>
                <PersonIcon fontSize="small" color="primary" />
            </IconButton>
            <IconButton size="small" component={Link} to={`/subscribers/${subscriber._id}/edit`}>
                <EditIcon fontSize="small" color="primary" />
            </IconButton>
            <IconButton size="small" disabled={!hasCognito} onClick={e => setMenuAnchorEl(e.currentTarget)}>
                <AlternateEmailIcon fontSize="small" color={hasCognito ? 'primary' : 'disabled'} />
            </IconButton>
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
                <MenuItem
                    onClick={() => {
                        handleMenuClose();
                        onLookupCognitoEmail(subscriber);
                    }}
                >
                    <ListItemIcon>
                        <AlternateEmailIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText>Look Up Cognito Email</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleMenuClose();
                        onCognitoGlobalSignOut(subscriber);
                    }}
                >
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>Sign Out All Sessions</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
};

const SubscriberCard = (props: {
    subscriber: any;
    onLookupCognitoEmail: (subscriber: any) => void;
    onCognitoGlobalSignOut: (subscriber: any) => void;
}) => {
    const { subscriber, onLookupCognitoEmail, onCognitoGlobalSignOut } = props;

    return (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={1}>
                <UsernameCell subscriber={subscriber} />
                {(subscriber.realName || subscriber.email) && (
                    <Stack spacing={0.25}>
                        {subscriber.realName && <Typography variant="body2">{subscriber.realName}</Typography>}
                        {subscriber.email && (
                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                {subscriber.email}
                            </Typography>
                        )}
                    </Stack>
                )}
                <Divider />
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                    <ServicesCell subscriber={subscriber} />
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <HostessTimestamp subscriber={subscriber} />
                        <Tooltip title="Copy ID">
                            <IconButton size="small" onClick={() => navigator.clipboard.writeText(String(subscriber._id))}>
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <ActionsCell
                            subscriber={subscriber}
                            onLookupCognitoEmail={onLookupCognitoEmail}
                            onCognitoGlobalSignOut={onCognitoGlobalSignOut}
                        />
                    </Stack>
                </Stack>
            </Stack>
        </Paper>
    );
};

const AdminUsersList = () => {
    const theme: Theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [searchParams, setSearchParams] = useSearchParams();
    const [offset, setOffset] = useState<number>(Number.parseInt(searchParams.get('offset') || '0', 10) || 0);
    const [limit, setLimit] = useState<number>(Number.parseInt(searchParams.get('limit') || '20', 10) || 20);
    const [filter, setFilter] = useState<string>(searchParams.get('filter') || '');
    const [filterInput, setFilterInput] = useState<string>(searchParams.get('filter') || '');
    const [loggedInOnly, setLoggedInOnly] = useState<boolean>(searchParams.get('loggedIn') === '1');
    const refFilter = useRef(null);
    const {
        data: subscribers,
        isFetching: subscribersLoading,
        refetch: subscribersRefetch,
    } = useSubscribersAdmin(offset, limit, filter, loggedInOnly);

    // Keep the search/filter/pagination state in the URL so it is restored when navigating back to this list
    useEffect(() => {
        const params = new URLSearchParams();

        if (filter) {
            params.set('filter', filter);
        }

        if (loggedInOnly) {
            params.set('loggedIn', '1');
        }

        if (offset) {
            params.set('offset', String(offset));
        }

        if (limit !== 20) {
            params.set('limit', String(limit));
        }

        setSearchParams(params, { replace: true });
    }, [filter, loggedInOnly, offset, limit, setSearchParams]);
    const { lookupCognitoEmail, lookupCognitoEmailLoading } = useCognitoEmailLookup();
    const { globalSignOut } = useCognitoGlobalSignOut();
    const dialog = useDialog();
    const [cognitoDialogOpen, setCognitoDialogOpen] = useState<boolean>(false);
    const [cognitoDialogSubscriber, setCognitoDialogSubscriber] = useState<any | null>(null);
    const [cognitoEmail, setCognitoEmail] = useState<string | null>(null);
    const [cognitoUsername, setCognitoUsername] = useState<string | null>(null);
    const [cognitoLookupError, setCognitoLookupError] = useState<string | null>(null);

    const handleLookupCognitoEmail = useCallback(
        async (subscriber: any) => {
            setCognitoDialogSubscriber(subscriber);
            setCognitoEmail(null);
            setCognitoUsername(subscriber.cognito?.username ?? null);
            setCognitoLookupError(null);
            setCognitoDialogOpen(true);

            const result = await lookupCognitoEmail(subscriber._id);

            if (result) {
                setCognitoEmail(result.email);
                setCognitoUsername(result.username);
            } else {
                setCognitoLookupError('Could not look up Cognito email.');
            }
        },
        [lookupCognitoEmail],
    );

    const handleCognitoGlobalSignOut = useCallback(
        async (subscriber: any) => {
            const confirmed = await dialog
                .confirm({
                    title: 'Sign Out All Sessions',
                    message: `Sign out all current sign ins for "${subscriber.username}"? This will invalidate all of their issued tokens and they will need to log in again on every device.`,
                    ok: {
                        color: 'error',
                        text: 'Sign Out',
                        variant: 'contained',
                    },
                    cancel: {
                        text: 'Cancel',
                        color: 'secondary',
                        variant: 'text',
                    },
                })
                .then(() => true)
                .catch(() => false);

            if (!confirmed) {
                return;
            }

            const success = await globalSignOut(subscriber._id);

            if (success) {
                dialog.alert({
                    title: 'Sign Out All Sessions',
                    message: `All sessions for "${subscriber.username}" have been signed out.`,
                });
            }
        },
        [dialog, globalSignOut],
    );

    const handleCloseCognitoDialog = () => {
        setCognitoDialogOpen(false);
        setCognitoDialogSubscriber(null);
        setCognitoEmail(null);
        setCognitoUsername(null);
        setCognitoLookupError(null);
    };

    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: 'username',
                headerName: 'Username',
                width: 230,
                headerClassName: 'boldHeader',
                renderCell: (params: GridRenderCellParams) => <UsernameCell subscriber={params.row} />,
            },
            {
                field: '_id',
                headerName: 'ID',
                width: 60,
                align: 'center',
                sortable: false,
                renderCell: (params: GridRenderCellParams) => (
                    <Tooltip title={<span style={{ fontFamily: 'monospace' }}>{String(params.id)}</span>}>
                        <IconButton size="small" onClick={() => navigator.clipboard.writeText(String(params.id))}>
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ),
            },
            {
                field: 'ofUserId',
                headerName: 'OF ID',
                width: 70,
                align: 'center',
                sortable: false,
                renderCell: (params: GridRenderCellParams) =>
                    params.row.ofUserId ? (
                        <Tooltip title={<span style={{ fontFamily: 'monospace' }}>{String(params.row.ofUserId)}</span>}>
                            <IconButton size="small" onClick={() => navigator.clipboard.writeText(String(params.row.ofUserId))}>
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ) : null,
            },
            { field: 'email', headerName: 'Email', width: 250 },
            { field: 'password', headerName: 'Password', width: 250 },
            { field: 'realName', headerName: 'Real Name', flex: 1, minWidth: 150 },
            {
                field: 'services',
                headerName: 'Services',
                width: 260,
                sortable: false,
                renderCell: (params: GridRenderCellParams) => <ServicesCell subscriber={params.row} />,
            },
            {
                field: 'lastHostessTimestamp',
                headerName: 'Hostess',
                width: 90,
                align: 'center',
                renderCell: (params: GridRenderCellParams) => <HostessTimestamp subscriber={params.row} />,
                sortable: true,
                sortComparator: (v1, v2) => {
                    return moment(v1).diff(moment(v2));
                },
            },
            {
                field: 'actions',
                headerName: 'Actions',
                width: 110,
                renderCell: (params: GridRenderCellParams) => (
                    <ActionsCell
                        subscriber={params.row}
                        onLookupCognitoEmail={handleLookupCognitoEmail}
                        onCognitoGlobalSignOut={handleCognitoGlobalSignOut}
                    />
                ),
                sortable: false,
            },
        ],
        [handleLookupCognitoEmail, handleCognitoGlobalSignOut],
    );

    const handleFilterSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (refFilter && refFilter.current) {
            setFilter(refFilter.current['value']);
            setOffset(0);
        }
    };

    const resetFilter = () => {
        setFilterInput('');

        if (filter && typeof filter === 'string' && filter !== '') {
            setFilter('');
            setOffset(0);
        }
    };

    const handleLoggedInOnlyChange = (checked: boolean) => {
        setLoggedInOnly(checked);
        setOffset(0);
    };

    return (
        <>
            <Grid container spacing={1} sx={{ width: '100%', mb: 2 }} alignItems="center">
                <Grid item xs={12} md>
                    <Paper component="form" variant="outlined" sx={{ p: '2px 4px', display: 'flex', alignItems: 'center', width: '100%' }}>
                        <InputBase
                            sx={{ ml: 1, flex: 1 }}
                            id="filter"
                            value={filterInput}
                            onChange={e => setFilterInput(e.target.value)}
                            inputRef={refFilter}
                            placeholder="Search"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={loggedInOnly}
                                    onChange={e => handleLoggedInOnlyChange(e.target.checked)}
                                />
                            }
                            label={
                                <Typography variant="body2" noWrap>
                                    Logged In
                                </Typography>
                            }
                            sx={{ mx: 0.5 }}
                        />
                        <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
                        <IconButton type="submit" onClick={handleFilterSubmit}>
                            <SearchIcon />
                        </IconButton>
                        <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
                        <IconButton color="primary" sx={{ p: '10px' }} onClick={resetFilter}>
                            <ClearIcon />
                        </IconButton>
                    </Paper>
                </Grid>
                <Grid item xs md="auto">
                    <Button variant="contained" endIcon={<AddIcon />} size="large" fullWidth component={Link} to="/subscribers/new">
                        Add Subscriber
                    </Button>
                </Grid>
                <Grid item xs="auto" md="auto">
                    <IconButton
                        size="large"
                        onClick={() => {
                            subscribersRefetch();
                        }}
                    >
                        <RefreshIcon />
                    </IconButton>
                </Grid>
            </Grid>

            {isMobile ? (
                <Box sx={{ width: '100%' }}>
                    {subscribersLoading && <LinearProgress sx={{ mb: 1 }} />}
                    <Stack spacing={1}>
                        {subscribers && subscribers.rows && subscribers.rows.length > 0
                            ? subscribers.rows.map((subscriber: any) => (
                                  <SubscriberCard
                                      key={subscriber._id}
                                      subscriber={subscriber}
                                      onLookupCognitoEmail={handleLookupCognitoEmail}
                                      onCognitoGlobalSignOut={handleCognitoGlobalSignOut}
                                  />
                              ))
                            : !subscribersLoading && (
                                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                      No users found
                                  </Typography>
                              )}
                    </Stack>
                    <TablePagination
                        component="div"
                        count={subscribers && subscribers.metadata && subscribers.metadata.total ? subscribers.metadata.total : 0}
                        page={Math.trunc(offset / limit)}
                        onPageChange={(_e, newPage) => setOffset(newPage * limit)}
                        rowsPerPage={limit}
                        onRowsPerPageChange={e => {
                            setLimit(parseInt(e.target.value, 10));
                            setOffset(0);
                        }}
                        rowsPerPageOptions={[10, 20, 40, 80, 100]}
                        labelRowsPerPage="Rows:"
                    />
                </Box>
            ) : (
                <Box
                    style={{
                        height: 'calc(100vh - 200px)',
                        width: '100%',
                    }}
                    sx={{
                        '.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },
                    }}
                >
                    <DataGridPro
                        rows={subscribers && subscribers.rows ? subscribers.rows : []}
                        columns={columns}
                        density={'compact'}
                        autoHeight
                        disableColumnFilter
                        rowCount={subscribers && subscribers.metadata && subscribers.metadata.total ? subscribers.metadata.total : 0}
                        getRowId={row => row._id}
                        pageSizeOptions={[10, 20, 40, 80, 100]}
                        pagination
                        paginationMode={'server'}
                        paginationModel={{
                            pageSize: limit,
                            page: Math.trunc(offset / limit),
                        }}
                        onPaginationModelChange={model => {
                            setOffset(model.pageSize * model.page);
                            setLimit(model.pageSize);
                        }}
                        initialState={{
                            pinnedColumns: {
                                left: ['username'],
                                right: ['lastHostessTimestamp', 'actions'],
                            },
                        }}
                        loading={subscribersLoading}
                        disableRowSelectionOnClick
                        slots={{
                            loadingOverlay: LinearProgress,
                        }}
                        sx={{
                            '& .boldHeader': {
                                fontWeight: 'bold',
                            },
                            '& .MuiDataGrid-cell': {
                                backgroundColor: theme.palette.common.white,
                            },
                            '& .MuiDataGrid-columnHeader': {
                                backgroundColor: theme.palette.common.white,
                            },
                            '& .MuiDataGrid-virtualScroller': {
                                backgroundColor: theme.palette.common.white,
                            },
                            '& .MuiDataGrid-footerContainer': {
                                backgroundColor: theme.palette.common.white,
                            },
                        }}
                    />
                </Box>
            )}

            <CognitoEmailLookupDialog
                open={cognitoDialogOpen}
                onClose={handleCloseCognitoDialog}
                subscriberUsername={cognitoDialogSubscriber?.username ?? ''}
                cognitoUsername={cognitoUsername}
                email={cognitoEmail}
                loading={lookupCognitoEmailLoading}
                error={cognitoLookupError}
            />
        </>
    );
};

export default AdminUsersList;
